import React, { useState } from 'react';
import { MessageSquare, Bug, Lightbulb, Gamepad2, Send, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const DISCORD_SERVER = process.env.REACT_APP_DISCORD_SUPPORT || 'https://discord.gg/robloxmart';

const CATEGORIES = [
  { value: 'recommend_game', label: 'Recommend a Game', icon: Gamepad2, desc: 'Suggest a new Roblox game to add to the marketplace' },
  { value: 'suggest_feature', label: 'Suggest a Feature', icon: Lightbulb, desc: 'Propose a new feature or improvement' },
  { value: 'report_glitch', label: 'Report a Bug/Glitch', icon: Bug, desc: 'Report a technical issue or bug you found' },
  { value: 'other', label: 'Other', icon: MessageSquare, desc: 'Anything else you\'d like to share' },
];

export default function Feedback() {
  const { user } = useAuth();
  const [form, setForm] = useState({ category: '', title: '', description: '', contact_info: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error('Please select a category');
    if (!form.title.trim()) return toast.error('Please provide a title');
    if (!form.description.trim()) return toast.error('Please describe your feedback');
    setLoading(true);
    try {
      await API.post('/feedback', form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally { setLoading(false); }
  };

  if (submitted) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center animate-fade-in max-w-md">
        <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
        <h2 className="font-rajdhani font-bold text-3xl uppercase mb-3">Thank You!</h2>
        <p className="text-rm-muted mb-6">Your feedback has been received. We review all submissions regularly and will act on the best suggestions.</p>
        <button onClick={() => { setSubmitted(false); setForm({ category: '', title: '', description: '', contact_info: '' }); }}
          className="btn-outline px-6 py-3 rounded-sm text-sm">Submit Another</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
      <div className="text-center mb-10">
        <h1 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase mb-3" data-testid="feedback-title">
          Feedback & <span className="text-rm-red">Support</span>
        </h1>
        <p className="text-rm-muted max-w-xl mx-auto">Help us improve RobloxMart. Report bugs, recommend games, suggest features — we read everything.</p>
      </div>

      {/* Discord CTA */}
      <div className="bg-[#5865F2]/10 border border-[#5865F2]/30 rounded-sm p-5 mb-8 flex flex-col sm:flex-row items-center gap-4">
        <div className="flex-1">
          <h3 className="font-rajdhani font-bold text-lg uppercase text-[#5865F2] mb-1">Join Our Discord</h3>
          <p className="text-rm-muted text-sm">For fast support, trading tips, middleman services, and community updates — join our Discord server.</p>
        </div>
        <a href={DISCORD_SERVER} target="_blank" rel="noopener noreferrer" data-testid="discord-link"
          className="flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] text-white px-5 py-3 rounded-sm font-rajdhani font-bold uppercase text-sm transition-colors shrink-0">
          <MessageSquare size={16} /> Join Discord <ExternalLink size={12} />
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Cards */}
        <div className="space-y-3">
          <h3 className="font-rajdhani font-bold text-sm uppercase tracking-widest text-rm-muted mb-3">Select Type</h3>
          {CATEGORIES.map(({ value, label, icon: Icon, desc }) => (
            <button key={value} onClick={() => setForm(f => ({ ...f, category: value }))} data-testid={`category-${value}`}
              className={`w-full text-left p-4 border rounded-sm transition-all
                ${form.category === value
                  ? 'border-rm-red bg-rm-red/10 text-white'
                  : 'border-rm-border bg-rm-card text-rm-muted hover:border-rm-border/80 hover:text-white'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={form.category === value ? 'text-rm-red' : 'text-rm-muted'} />
                <span className="font-rajdhani font-bold text-sm uppercase">{label}</span>
              </div>
              <p className="text-xs opacity-70">{desc}</p>
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-rm-card border border-rm-border rounded-sm p-6 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Title *</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief title of your feedback..."
                className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-4 py-3 rounded-sm text-sm"
                data-testid="feedback-title-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Description *</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={form.category === 'recommend_game' ? 'Game name, why it should be added, typical items traded...'
                  : form.category === 'report_glitch' ? 'Describe the bug: what happened, what you expected, steps to reproduce...'
                  : form.category === 'suggest_feature' ? 'Describe the feature, how it would work, why it would be useful...'
                  : 'Your message...'}
                rows={6} data-testid="feedback-description"
                className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-4 py-3 rounded-sm text-sm resize-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">
                Your Contact Info <span className="text-zinc-600 normal-case tracking-normal">(optional — Discord/email so we can follow up)</span>
              </label>
              <input type="text" value={form.contact_info} onChange={e => setForm(f => ({ ...f, contact_info: e.target.value }))}
                placeholder="Discord: YourName#1234 or Email: you@example.com (optional)"
                className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-4 py-3 rounded-sm text-sm"
                data-testid="feedback-contact" />
            </div>
            {user && (
              <p className="text-xs text-rm-muted">Submitting as: <span className="text-white">{user.username}</span></p>
            )}
            <button type="submit" disabled={loading || !form.category} data-testid="feedback-submit"
              className="w-full btn-primary py-4 rounded-sm text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              <Send size={16} /> {loading ? 'Submitting...' : 'Send Feedback'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
